import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors and profile completion errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
        window.location.href = '/login';
      }
    }
    
    // Handle profile incomplete errors (403 with profile_incomplete error)
    if (error.response?.status === 403 && error.response?.data?.detail?.error === 'profile_incomplete') {
      // Store the error details for the UI to handle
      const profileError = error.response.data.detail;
      // Dispatch a custom event that components can listen to
      window.dispatchEvent(new CustomEvent('profileIncomplete', { 
        detail: profileError 
      }));
    }
    
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getSession: (sessionId) => api.get('/auth/session', { headers: { 'X-Session-ID': sessionId } }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  // OTP Verification
  sendEmailOtp: (email) => api.post('/auth/send-email-otp', null, { params: { email } }),
  verifyEmailOtp: (email, otp) => api.post('/auth/verify-email-otp', null, { params: { email, otp } }),
  sendPhoneOtp: (phone, userId) => api.post('/auth/send-phone-otp', null, { params: { phone, user_id: userId } }),
  verifyPhoneOtp: (userId, otp) => api.post('/auth/verify-phone-otp', null, { params: { user_id: userId, otp } }),
  // Password Reset
  forgotPassword: (email) => api.post('/auth/forgot-password', null, { params: { email } }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', null, { params: { token, new_password: newPassword } }),
  verifyResetToken: (token) => api.get('/auth/verify-reset-token', { params: { token } }),
};

// Users/Talents (Bookees)
export const userApi = {
  updateProfile: (data) => api.put('/profile', data),
  getTalents: (params) => api.get('/talents', { params }),
  searchTalents: (params) => api.get('/talents/search', { params }),
  getTalent: (id) => api.get(`/talents/${id}`),
  becomeTalent: () => api.post('/become-talent'),
  getCategories: (params) => api.get('/categories', { params }),
  getFeaturedTalents: () => api.get('/featured-talents'),
  // Smart Search
  smartSearch: (query, location, date) => api.get('/search/smart', { params: { query, location, date } }),
  // Profile Boost
  boostProfile: (data) => api.post('/profile/boost', data),
  getBoostStatus: () => api.get('/profile/boost/status'),
  // Email Preferences
  getEmailPreferences: () => api.get('/profile/email-preferences'),
  updateEmailPreferences: (data) => api.put('/profile/email-preferences', data),
};

// Verification (Stripe Identity)
export const verificationApi = {
  createSession: (returnUrl) => api.post('/verification/create-session', { return_url: returnUrl }),
  getStatus: () => api.get('/verification/status'),
  checkStatus: () => api.post('/verification/check'),
  adminApprove: (userId) => api.post(`/verification/admin-approve/${userId}`),
};

// Profile Completion
export const profileApi = {
  getCompletionStatus: () => api.get('/profile/completion-status'),
  updateCompletion: (data) => api.put('/profile/complete', data),
  addPaymentMethod: () => api.post('/profile/add-payment-method'),
};

// Explore
export const exploreApi = {
  getRandom: (count = 9) => api.get('/explore/random', { params: { count } }),
  refresh: (count = 9) => api.post('/explore/refresh', null, { params: { count } }),
};

// Push Notifications
export const pushApi = {
  subscribe: (subscription) => api.post('/push/subscribe', subscription),
  unsubscribe: () => api.delete('/push/unsubscribe'),
  getVapidKey: () => api.get('/push/vapid-key'),
};

// Admin
export const adminApi = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (params) => api.get('/admin/users', { params }),
  verifyUser: (userId, verified) => api.put(`/admin/users/${userId}/verify`, null, { params: { verified } }),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  toggleAdmin: (userId, isAdmin) => api.put(`/admin/users/${userId}/admin`, null, { params: { is_admin: isAdmin } }),
  getBookings: (params) => api.get('/admin/bookings', { params }),
  updateBookingStatus: (bookingId, status) => api.put(`/admin/bookings/${bookingId}/status`, null, { params: { status } }),
  getFeatured: () => api.get('/admin/featured'),
  updateFeatured: (data) => api.put('/admin/featured', null, { params: data }),
  getCategories: () => api.get('/admin/categories'),
  addCategory: (category) => api.post('/admin/categories', null, { params: { category } }),
  deleteCategory: (category) => api.delete(`/admin/categories/${category}`),
};

// Venues
export const venueApi = {
  create: (data) => api.post('/venues', data),
  getAll: (params) => api.get('/venues', { params }),
  getMy: () => api.get('/venues/my'),
  get: (venueId) => api.get(`/venues/${venueId}`),
  update: (venueId, data) => api.put(`/venues/${venueId}`, data),
  delete: (venueId) => api.delete(`/venues/${venueId}`),
  getMetadata: () => api.get('/venues/metadata'),
  addVenueRole: () => api.post('/user/add-venue-role'),
  getFeatured: (limit = 6) => api.get('/venues/featured', { params: { limit } }),
};

// Equipment/Rentals
export const equipmentApi = {
  getFeatured: (limit = 6) => api.get('/featured/equipment', { params: { limit } }),
};

// Calendar Events
export const calendarApi = {
  createEvent: (data) => api.post('/calendar/events', data),
  getMyEvents: () => api.get('/calendar/events/my'),
  getPublicEvents: (limit) => api.get('/calendar/events/public', { params: { limit } }),
  getEvent: (eventId) => api.get(`/calendar/events/${eventId}`),
  updateEvent: (eventId, data) => api.put(`/calendar/events/${eventId}`, data),
  deleteEvent: (eventId) => api.delete(`/calendar/events/${eventId}`),
  featureEvent: (eventId, featured) => api.post(`/calendar/events/${eventId}/feature`, null, { params: { featured } }),
  getFeatured: () => api.get('/calendar/featured'),
};

// Profile Boost
export const boostApi = {
  purchase: (duration_days, boost_type = 'standard') => api.post('/profile/boost', { duration_days, boost_type }),
  getStatus: () => api.get('/profile/boost/status'),
  activate: (boostId) => api.post(`/profile/boost/activate/${boostId}`),
};

// Search Autocomplete
export const autocompleteApi = {
  talents: (query, limit = 8) => api.get('/autocomplete/talents', { params: { q: query, limit } }),
  locations: (query, limit = 6) => api.get('/autocomplete/locations', { params: { q: query, limit } }),
};

// Services
export const serviceApi = {
  create: (data) => api.post('/services', data),
  getMy: () => api.get('/services/my'),
  getByTalent: (talentId) => api.get(`/services/talent/${talentId}`),
  update: (serviceId, data) => api.put(`/services/${serviceId}`, data),
  delete: (serviceId) => api.delete(`/services/${serviceId}`),
  getMetadata: () => api.get('/services/metadata'),
};

// Deliverables
export const deliverableApi = {
  create: (data) => api.post('/deliverables', data),
  getByBooking: (bookingId) => api.get(`/deliverables/booking/${bookingId}`),
  getMy: () => api.get('/deliverables/my'),
  accept: (deliverableId) => api.put(`/deliverables/${deliverableId}/accept`),
};

// Platform
export const platformApi = {
  getFees: () => api.get('/platform/fees'),
  getCancellationPolicies: () => api.get('/platform/cancellation-policies'),
  getProfessionalServices: () => api.get('/platform/professional-services'),
};

// Availability
export const availabilityApi = {
  set: (slots) => api.post('/availability', slots),
  get: (talentId) => api.get(`/availability/${talentId}`),
  getBookedSlots: (talentId, date) => api.get(`/availability/${talentId}/booked-slots`, { params: { date } }),
};

// Bookings
export const bookingApi = {
  create: (data) => api.post('/bookings', data),
  createBatch: (data) => api.post('/bookings/batch', data),
  getMy: () => api.get('/bookings/my'),
  updateStatus: (id, status) => api.put(`/bookings/${id}/status`, null, { params: { status } }),
  generateContract: (id, useTemplate = false) => api.post(`/bookings/${id}/generate-contract`, null, { params: { use_template: useTemplate } }),
  requestPriceChange: (id, newAmount, reason) => api.put(`/bookings/${id}/price-change`, { new_amount: newAmount, reason }),
  approvePriceChange: (bookingId, requestId, approved) => api.put(`/bookings/${bookingId}/approve-price-change/${requestId}`, null, { params: { approved } }),
  // Custom Line Items / Additional Fees
  addFee: (bookingId, data) => api.post(`/bookings/${bookingId}/fees`, data),
  approveFee: (bookingId, feeId, approved) => api.put(`/bookings/${bookingId}/fees/${feeId}/approve`, null, { params: { approved } }),
  removeFee: (bookingId, feeId) => api.delete(`/bookings/${bookingId}/fees/${feeId}`),
  getFees: (bookingId) => api.get(`/bookings/${bookingId}/fees`),
  // Tip
  addTip: (bookingId, amount, atCheckout = true) => api.post(`/bookings/${bookingId}/tip`, { amount, at_checkout: atCheckout }),
};

// Opportunities
export const opportunityApi = {
  create: (data) => api.post('/opportunities', data),
  getAll: (params) => api.get('/opportunities', { params }),
  get: (id) => api.get(`/opportunities/${id}`),
  delete: (id) => api.delete(`/opportunities/${id}`),
};

// Reviews
export const reviewApi = {
  create: (data) => api.post('/reviews', data),
  getByTalent: (talentId, limit = 20, skip = 0) => api.get(`/reviews/talent/${talentId}`, { params: { limit, skip } }),
  getMy: () => api.get('/reviews/my'),
};

// Loyalty & Discounts
export const loyaltyApi = {
  getStatus: (talentId) => api.get(`/loyalty/status/${talentId}`),
  getMyDiscounts: () => api.get('/loyalty/my-discounts'),
};

// Repeat Client Pricing
export const repeatClientApi = {
  updateSettings: (data) => api.put('/profile/repeat-client-discount', data),
  checkDiscount: (talentId) => api.get(`/profile/${talentId}/repeat-discount`),
};

// Support Tickets
export const supportApi = {
  createTicket: (data) => api.post('/support/tickets', data),
  getMyTickets: () => api.get('/support/tickets'),
  getTicket: (ticketId) => api.get(`/support/tickets/${ticketId}`),
  replyToTicket: (ticketId, message) => api.post(`/support/tickets/${ticketId}/reply`, { message }),
  closeTicket: (ticketId) => api.post(`/support/tickets/${ticketId}/close`),
};

// Favorites
export const favoriteApi = {
  add: (talentId) => api.post('/favorites', { talent_id: talentId }),
  remove: (talentId) => api.delete(`/favorites/${talentId}`),
  getAll: () => api.get('/favorites'),
  check: (talentId) => api.get(`/favorites/check/${talentId}`),
};

// Reports & Blocks
export const reportApi = {
  report: (reportedUserId, reason, description) => api.post('/reports', { reported_user_id: reportedUserId, reason, description }),
  block: (userId) => api.post('/block', { blocked_user_id: userId }),
  unblock: (userId) => api.delete(`/block/${userId}`),
  getBlocked: () => api.get('/blocked'),
};

// Payments
export const paymentApi = {
  createCheckout: (data) => api.post('/payments/checkout', data),
  checkStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  // PayPal
  createPayPalOrder: (data) => api.post('/payments/paypal/create-order', data),
  createPayPalBatchOrder: (data) => api.post('/payments/paypal/create-batch-order', data),
  executePayPalPayment: (paymentId, payerId) => api.post('/payments/paypal/execute', null, { params: { payment_id: paymentId, payer_id: payerId } }),
  getPaymentMethods: () => api.get('/payments/methods'),
  // Subscriptions
  createSubscriptionStripe: (data) => api.post('/subscriptions/stripe/checkout', data),
  createSubscriptionPayPal: (data) => api.post('/subscriptions/paypal/checkout', data),
  getMySubscription: () => api.get('/subscriptions/my'),
  cancelSubscription: () => api.post('/subscriptions/cancel'),
};

// Promo Codes
export const promoApi = {
  validate: (code, bookingId) => api.post('/promo/validate', { code, booking_id: bookingId }),
  apply: (code, bookingId) => api.post('/promo/apply', { code, booking_id: bookingId }),
};

// Stats
export const statsApi = {
  getDashboard: () => api.get('/stats/dashboard'),
};

// Stripe Connect (Marketplace Payouts)
export const stripeConnectApi = {
  createAccount: () => api.post('/stripe-connect/create-account'),
  getOnboardingLink: (returnUrl, refreshUrl) => api.post('/stripe-connect/onboarding-link', { return_url: returnUrl, refresh_url: refreshUrl }),
  getStatus: () => api.get('/stripe-connect/status'),
  getDashboardLink: () => api.get('/stripe-connect/dashboard-link'),
  getBalance: () => api.get('/stripe-connect/balance'),
  getEarnings: () => api.get('/stripe-connect/earnings'),
};

// Role Management (Dual Roles)
export const roleApi = {
  getRoles: () => api.get('/user/roles'),
  addRole: (role) => api.post('/user/add-role', { role }),
  switchRole: (role) => api.post('/user/switch-role', { role }),
};

// Vanity URLs / Screen Names
export const vanityApi = {
  checkAvailability: (screenName) => api.get(`/profile/check-screen-name/${screenName}`),
  updateScreenName: (screenName) => api.put('/profile/screen-name', null, { params: { screen_name: screenName } }),
  getProfileByVanity: (identifier) => api.get(`/profile/${identifier}`),
};

// Payouts (Admin/Automated)
export const payoutApi = {
  getPending: () => api.get('/payouts/pending'),
  release: (bookingId) => api.post(`/payouts/release/${bookingId}`),
  autoRelease: () => api.post('/payouts/auto-release'),
};

// Messages
export const messageApi = {
  getConversations: () => api.get('/messages/conversations'),
  getMessages: (conversationId) => api.get(`/messages/${conversationId}`),
  sendMessage: (data) => api.post('/messages', data),
  getUnreadCount: () => api.get('/messages/unread/count'),
  startConversation: (recipientId) => api.post(`/messages/start/${recipientId}`),
  searchMessages: (query, conversationId) => api.get('/messages/search', { params: { query, conversation_id: conversationId } }),
  checkContact: (content) => api.post('/messages/check-contact', null, { params: { content } }),
  markRead: (messageId) => api.put(`/messages/${messageId}/read`),
};

// File Uploads
export const fileApi = {
  uploadVoice: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/voice', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadAttachment: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/attachment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadProfilePicture: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/profile-picture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadPortfolio: (file, itemType = 'image') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/upload/portfolio?item_type=${itemType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getStorageInfo: () => api.get('/files/storage'),
  deleteFile: (fileId) => api.delete(`/files/${fileId}`),
  downloadAllVoice: () => api.post('/files/download-all'),
};

export default api;
